import { Capacitor, registerPlugin } from '@capacitor/core';
import { API_BASE_URL } from '../services/api';
import { isTWA } from './platform';

/**
 * Where did this person come from? Captured on the first page load of a
 * visit (first touch, never overwritten), sent with the signup request, and
 * stamped on the account as User.acquisition. On iOS the app also hands the
 * backend an Apple AdServices token after sign-in so Apple Search Ads
 * installs can be told apart from organic ones.
 */
const KEY = 'ft_attribution';
const ASA_SENT = 'ft_asa_attribution_sent';

export interface CapturedAttribution {
  source?: string; medium?: string; campaign?: string; content?: string; term?: string;
  referrer?: string; landing?: string; capturedAt: string;
}

export function captureAttribution(): void {
  try {
    if (localStorage.getItem(KEY)) return;
    const p = new URLSearchParams(window.location.search);
    const pick = (...keys: string[]) => { for (const k of keys) { const v = p.get(k); if (v) return v.slice(0, 120); } return undefined; };
    const a: CapturedAttribution = {
      source: pick('utm_source', 'ref', 'via', 'src'),
      medium: pick('utm_medium'),
      campaign: pick('utm_campaign', 'campaign'),
      content: pick('utm_content'),
      term: pick('utm_term'),
      referrer: document.referrer ? document.referrer.slice(0, 300) : undefined,
      landing: window.location.pathname.slice(0, 200),
      capturedAt: new Date().toISOString(),
    };
    // Only remember a visit that carries a signal; a bare open keeps looking for one.
    if (a.source || a.campaign || a.referrer) localStorage.setItem(KEY, JSON.stringify(a));
  } catch { /* never block the app */ }
}

/** Which app shell the person is in: the iOS app, the Play Store app (TWA), or a browser. */
export function signupPlatform(): 'ios' | 'android' | 'web' {
  try {
    if (Capacitor.isNativePlatform()) return Capacitor.getPlatform() === 'ios' ? 'ios' : 'android';
    if (isTWA()) return 'android';
  } catch { /* fall through */ }
  return 'web';
}

/** The device's operating system, so "web" can still be told apart as an Android phone in Chrome vs a desktop. */
export function deviceOS(): 'ios' | 'android' | 'windows' | 'mac' | 'other' {
  try {
    const ua = navigator.userAgent || '';
    if (/iPhone|iPad|iPod/i.test(ua) || (/Macintosh/i.test(ua) && navigator.maxTouchPoints > 1)) return 'ios';
    if (/Android/i.test(ua)) return 'android';
    if (/Windows/i.test(ua)) return 'windows';
    if (/Macintosh|Mac OS/i.test(ua)) return 'mac';
  } catch { /* fall through */ }
  return 'other';
}

/** The object attached to every signup request (see api.ts interceptor). */
export function attributionForSignup(): Record<string, unknown> {
  let captured: Partial<CapturedAttribution> = {};
  try { captured = JSON.parse(localStorage.getItem(KEY) || '{}'); } catch { /* ignore */ }
  let onboardingSessionId: string | undefined;
  try { onboardingSessionId = sessionStorage.getItem('fueltron_onboarding_session') || undefined; } catch { /* ignore */ }
  return { ...captured, platform: signupPlatform(), os: deviceOS(), onboardingSessionId };
}

// ── Apple Search Ads (iOS app only; needs the AttributionPlugin in the native shell) ──
interface AttributionPlugin { adServicesToken(): Promise<{ token: string }>; }
const Native = registerPlugin<AttributionPlugin>('Attribution');

/** Once per install: hand the backend Apple's attribution token. Safe to call on every launch. */
export async function sendAppleAdsAttribution(): Promise<void> {
  try {
    if (Capacitor.getPlatform() !== 'ios') return;
    if (localStorage.getItem(ASA_SENT) === '1') return;
    const token = localStorage.getItem('token');
    if (!token) return;
    const { token: asaToken } = await Native.adServicesToken();
    if (!asaToken) return;
    const res = await fetch(`${API_BASE_URL}/attribution/asa`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ token: asaToken }),
    });
    const data = await res.json().catch(() => ({}));
    // Apple can answer "not yet" for a few seconds after install; try again next launch.
    if (data?.resolved) localStorage.setItem(ASA_SENT, '1');
  } catch { /* the plugin is absent on old builds; ignore */ }
}
